import React, { useEffect, useState } from 'react'
import { api, formatDateTime } from '../../utils/api'

/**
 * Business facts: the agreed policy wording Prism's AI reply drafts read
 * (inbox deflection W2). One change here reaches every draft. The website
 * pages and help articles copy the same wording by hand.
 */
export default function BusinessFacts() {
  const [facts, setFacts] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => { loadFacts() }, [])

  async function loadFacts() {
    try {
      const response = await api.get('/api/business-facts')
      setFacts(response.facts || [])
      setError(null)
    } catch (err) {
      setError(`Could not load the facts: ${err.message}`)
    }
  }

  async function saveFact(fact) {
    await api.put('/api/business-facts', fact)
    await loadFacts()
  }

  return (
    <div className="card" style={{ padding: 16 }}>
      <h2 style={{ marginTop: 0 }}>Business facts</h2>
      <p style={{ color: 'var(--muted)', maxWidth: 680 }}>
        The wording Prism's AI reply drafts use for our policies. Change a fact here and every new draft uses it.
        The website pages and help articles carry the same wording, so copy a change there too.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      {!facts && !error && <p>Loading…</p>}
      {facts && (
        <div style={{ display: 'grid', gap: 12 }}>
          {facts.map(fact => <FactRow key={fact.key} fact={fact} onSave={saveFact} />)}
          <NewFactRow onSave={saveFact} />
        </div>
      )}
    </div>
  )
}

function FactRow({ fact, onSave }) {
  const [value, setValue] = useState(fact.value)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const changed = value.trim() !== fact.value

  async function handleSave() {
    setSaving(true)
    setMessage(null)
    try {
      await onSave({ key: fact.key, value })
      setMessage('Saved')
    } catch (err) {
      setMessage(`Not saved: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ border: '1px solid var(--border, #e5e7eb)', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <label htmlFor={`fact-${fact.key}`} style={{ fontWeight: 600 }}>{fact.key}</label>
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>
          {fact.lastChange
            ? `Last changed by ${fact.lastChange.userName || 'someone'} on ${formatDateTime(fact.lastChange.at)}`
            : 'Not changed in Prism yet'}
        </span>
      </div>
      <textarea
        id={`fact-${fact.key}`}
        className="form-input"
        rows={2}
        value={value}
        onChange={e => setValue(e.target.value)}
        style={{ width: '100%', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={!changed || saving || !value.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {message && <span style={{ fontSize: 12 }}>{message}</span>}
      </div>
    </div>
  )
}

function NewFactRow({ onSave }) {
  const [key, setKey] = useState('')
  const [value, setValue] = useState('')
  const [message, setMessage] = useState(null)

  async function handleAdd() {
    setMessage(null)
    try {
      await onSave({ key, value })
      setKey('')
      setValue('')
    } catch (err) {
      setMessage(`Not added: ${err.message}`)
    }
  }

  return (
    <div style={{ border: '1px dashed var(--border, #e5e7eb)', borderRadius: 8, padding: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Add a fact</div>
      <input
        className="form-input"
        placeholder="Name, for example Phone orders"
        value={key}
        onChange={e => setKey(e.target.value)}
        aria-label="New fact name"
        style={{ width: '100%', marginBottom: 6 }}
      />
      <textarea
        className="form-input"
        rows={2}
        placeholder="The wording staff and the AI should use"
        value={value}
        onChange={e => setValue(e.target.value)}
        aria-label="New fact wording"
        style={{ width: '100%', resize: 'vertical' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
        <button className="btn btn-secondary btn-sm" onClick={handleAdd} disabled={!key.trim() || !value.trim()}>
          Add
        </button>
        {message && <span style={{ fontSize: 12 }}>{message}</span>}
      </div>
    </div>
  )
}
